/**
 * SelectedContextBar - removable context chips shown above the AI chat input.
 *
 * Renders each ExplicitContextItem as an individually-dismissible chip.
 * The chip area wraps across multiple lines when items accumulate.
 * Hidden when the item list is empty.
 *
 * Architecture notes:
 *   - Items come from `explicitContextItems` in ChatState, NOT from SelectedContext.
 *   - Each chip has its own ×  remove button (no single global "clear all").
 *   - An optional "Clear all" button appears at the trailing end for convenience.
 *   - Source domain and context type are typed - see ExplicitContextItem.
 *
 * Scope model:
 *   pageScope is NOT shown as a chip here - it is a secondary context hint
 *   injected per-run by AiChatPane.handleSend() and is not a user-visible scope.
 *   Only explicit user-selected items are rendered here.
 *
 * Future expansion:
 *   When Files / Docs / Boards / Pipelines menus produce ExplicitContextItems,
 *   they will appear here automatically - no rendering changes required.
 */

import type { ExplicitContextItem } from '@protoss/shared';
import { Cross1Icon, IconSize, Tooltip } from '@protoss/ui';

interface SelectedContextBarProps {
  items: ExplicitContextItem[];
  onRemove: (itemId: string) => void;
  onClearAll?: () => void;
  draftItem?: ExplicitContextItem | null;
  onConfirmDraft?: () => void;
  onClearDraft?: () => void;
}

/**
 * Build data needed to render a domain badge prefix for the chip.
 * Returns a short domain indicator visible for disambiguation when multiple
 * domains are present in the same chip list.
 */
function domainBadge(item: ExplicitContextItem): string {
  switch (item.domain) {
    case 'repo':
      return '';
    case 'files':
      return 'file: ';
    case 'docs':
      return 'doc: ';
    case 'boards':
      return 'board: ';
    case 'pipelines':
      return 'pipe: ';
    default:
      return '';
  }
}

export function SelectedContextBar({
  items,
  onRemove,
  onClearAll,
  draftItem,
  onConfirmDraft,
  onClearDraft,
}: SelectedContextBarProps) {
  if (items.length === 0 && !draftItem) return null;

  const showClearAll = !!onClearAll && items.length > 1;

  return (
    <div className="ai-chat-context-bar" role="region" aria-label="Selected context">
      <div className="ai-chat-context-bar__chips">
        {items.map((item) => (
          <Tooltip.Root key={item.id}>
            <Tooltip.Trigger asChild>
              <span className="ai-chat-chip">
                <span className="ai-chat-chip__label">
                  {domainBadge(item)}
                  {item.displayLabel}
                </span>
                <button
                  type="button"
                  className="ai-chat-chip__remove"
                  onClick={() => onRemove(item.id)}
                  aria-label={`Remove context: ${item.displayLabel}`}
                >
                  <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
                </button>
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="top" sideOffset={6} className="ui-tooltip">
                {item.displayLabel}
                <Tooltip.Arrow className="ui-tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        ))}
        {draftItem && (
          <Tooltip.Root>
            <Tooltip.Trigger asChild>
              <span
                className="ai-chat-chip ai-chat-chip--draft"
                onClick={onConfirmDraft}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => e.key === 'Enter' && onConfirmDraft?.()}
                aria-label={`Add to context: ${draftItem.displayLabel}`}
              >
                <span className="ai-chat-chip__label">
                  {domainBadge(draftItem)}
                  {draftItem.displayLabel}
                </span>
                <button
                  type="button"
                  className="ai-chat-chip__remove"
                  onClick={(e) => {
                    e.stopPropagation();
                    onClearDraft?.();
                  }}
                  aria-label={`Dismiss: ${draftItem.displayLabel}`}
                >
                  <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
                </button>
              </span>
            </Tooltip.Trigger>
            <Tooltip.Portal>
              <Tooltip.Content side="top" sideOffset={6} className="ui-tooltip">
                Click to add to context
                <Tooltip.Arrow className="ui-tooltip-arrow" />
              </Tooltip.Content>
            </Tooltip.Portal>
          </Tooltip.Root>
        )}
      </div>

      {showClearAll && (
        <Tooltip.Simple label="Clear all context">
          <button
            type="button"
            className="ai-chat-context-bar__clear"
            onClick={onClearAll}
            aria-label="Clear all context"
          >
            <Cross1Icon width={IconSize.xs} height={IconSize.xs} aria-hidden="true" />
          </button>
        </Tooltip.Simple>
      )}
    </div>
  );
}
